/**
 * API job status → UI job status.
 *
 * The API reports what is stored in `jobs.status`. The full list lives
 * in `shared/python-core/flow_shared_core/job_status.py`:
 *
 *   queued                not picked up by a runner yet
 *   running               a runner is driving the portal
 *   awaiting_approval     blocked at a checkpoint, waiting for approve / deny
 *   waiting_for_human     blocked because the agent, a scripted step or
 *                         the CAPTCHA detector asked for a person
 *   paused_for_takeover   a supervisor holds control of the browser
 *   complete | failed | cancelled | completed_by_human   finished
 *
 * The UI has its own, smaller display vocabulary (`JobStatus` in
 * mockData: `success`, `approval`, `takeover`, `human`, …) and every
 * screen string-matches on it — pill colours, the active / recent
 * split, the attention rail, usage totals. This module is the single
 * place the translation happens. Screens keep matching on the UI
 * vocabulary; nothing outside this file should compare against a raw
 * API status, except through the helpers exported here.
 *
 * Two API statuses share the UI bucket `takeover` (a person is needed
 * at, or is at, the browser): `waiting_for_human` and
 * `paused_for_takeover`. They are told apart by LABEL, with
 * `jobStatusLabel(ui, apiStatus)`, so the dashboard can show which it
 * is without a new colour token.
 *
 * "Finished OK" has been stored under three spellings (`complete`,
 * `success`, `completed`). The API now always sends `complete`, but all
 * three are accepted here so the UI is correct against an older API
 * build too.
 */
import type { JobStatus } from "@/lib/mockData";

const API_TO_UI: Readonly<Record<string, JobStatus>> = {
  queued: "queued",
  running: "running",

  // Finished OK — canonical spelling first, then the two legacy ones.
  complete: "success",
  success: "success",
  completed: "success",

  completed_by_human: "human",
  human: "human",

  failed: "failed",
  cancelled: "failed",

  paused_for_approval: "approval",
  awaiting_approval: "approval",
  approval: "approval",

  // A person is needed at the browser, or is already there.
  waiting_for_human: "takeover",
  paused_for_takeover: "takeover",
  takeover: "takeover",
};

const warned = new Set<string>();

/**
 * Status the UI does not recognise. `queued` is the neutral choice: it
 * claims no outcome (unlike `failed` or `success`) and prompts no
 * action — showing an unknown state as failed could lead someone to
 * re-queue a job that is still working in a payer portal.
 */
export const UNKNOWN_STATUS_FALLBACK: JobStatus = "queued";

function normalize(raw: unknown): string {
  return typeof raw === "string" ? raw.trim().toLowerCase() : "";
}

export function toUiJobStatus(raw: unknown): JobStatus {
  const key = normalize(raw);
  const mapped = Object.prototype.hasOwnProperty.call(API_TO_UI, key) ? API_TO_UI[key] : undefined;
  if (mapped !== undefined) return mapped;
  if (!warned.has(key)) {
    warned.add(key);
    // The status string is a fixed backend token, not patient data.
    console.warn(`[flow] unrecognised job status from API: "${key.slice(0, 40)}"`);
  }
  return UNKNOWN_STATUS_FALLBACK;
}

/**
 * Apply `toUiJobStatus` to a job-shaped object from the API. The raw
 * status is kept as `apiStatus` so labels can tell apart API statuses
 * that share a UI bucket.
 */
export function withUiStatus<T extends { status: unknown }>(
  job: T,
): T & { status: JobStatus; apiStatus: string } {
  return { ...job, status: toUiJobStatus(job.status), apiStatus: normalize(job.status) };
}

const UI_LABEL: Readonly<Record<JobStatus, string>> = {
  queued: "Queued",
  running: "Running",
  approval: "Needs approval",
  takeover: "Waiting for a person",
  failed: "Failed",
  success: "Completed",
  human: "Completed by a person",
};

/**
 * The label to show for a job. `apiStatus` (the raw API status, when
 * known) refines the two buckets that hold more than one API status.
 */
export function jobStatusLabel(status: JobStatus, apiStatus?: string | null): string {
  const api = normalize(apiStatus);
  if (api === "paused_for_takeover") return "Supervisor in control";
  if (api === "waiting_for_human") return "Waiting for a person";
  if (api === "cancelled") return "Cancelled";
  return UI_LABEL[status];
}

const ON_RUNNER = new Set([
  "running",
  "awaiting_approval",
  "paused_for_approval",
  "waiting_for_human",
  "paused_for_takeover",
]);

/** A runner holds the job: it can be watched, taken over and stopped. */
export function isOnRunnerStatus(apiStatus: string | null | undefined): boolean {
  return ON_RUNNER.has(normalize(apiStatus));
}

/** The job is blocked until a person acts (approval, help, or control). */
export function isWaitingOnPerson(apiStatus: string | null | undefined): boolean {
  const api = normalize(apiStatus);
  return ON_RUNNER.has(api) && api !== "running";
}
